import { useEffect, useState } from "react";
import { Bookmark, Heart, MessageCircle, Send } from "lucide-react";

interface InstagramMockupProps {
  variant: "feed" | "story";
  previewUrl: string;
  previewKey: string;
  clubName: string;
  crestUrl?: string | null;
  title: string;
  caption?: string;
}

interface ScaledPreviewProps {
  src: string;
  title: string;
  height: 1350 | 1920;
  eager?: boolean;
}

export function ScaledLivePreview({ src, title, height, eager = false }: ScaledPreviewProps) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => setLoaded(false), [src]);

  return (
    <div
      className={`relative w-full overflow-hidden bg-muted ${height === 1920 ? "aspect-[9/16]" : "aspect-[4/5]"}`}
    >
      {!loaded ? <div className="absolute inset-0 bg-muted" aria-hidden="true" /> : null}
      <img
          src={src}
          alt={title}
          width="1080"
          height={height}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
          onLoad={() => setLoaded(true)}
      />
    </div>
  );
}

function Avatar({ crestUrl, clubName }: Pick<InstagramMockupProps, "crestUrl" | "clubName">) {
  return (
    <span className="flex aspect-square shrink-0 items-center justify-center overflow-hidden rounded-full bg-instagram-muted">
      {crestUrl ? <img src={crestUrl} alt={`${clubName} Wappen`} className="h-full w-full object-contain" /> : null}
    </span>
  );
}

export default function InstagramMockup({
  variant,
  previewUrl,
  previewKey,
  clubName,
  crestUrl,
  title,
}: InstagramMockupProps) {
  if (variant === "story") {
    return (
      <div className="relative mx-auto aspect-[9/16] w-full overflow-hidden rounded-2xl border-[10px] border-instagram-shell bg-instagram-shell shadow-sm md:aspect-[5/7]">
        <div className="flex h-full w-full items-center justify-center overflow-hidden bg-instagram-shell">
          <div className="relative aspect-[9/16] h-full max-w-full overflow-hidden text-instagram-screen">
            <ScaledLivePreview key={previewKey} src={previewUrl} title={title} height={1920} />
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[72px] bg-gradient-to-b from-instagram-overlay to-transparent" />
            <div className="absolute inset-x-[4%] top-[2%] z-20 h-0.5 overflow-hidden rounded-full bg-instagram-screen/40">
              <div className="h-full w-[68%] rounded-full bg-instagram-screen" />
            </div>
            <div className="absolute inset-x-[5%] top-[4%] z-20 flex items-start gap-2 text-[14px] text-instagram-screen drop-shadow-md">
              <span className="w-7 shrink-0 rounded-full ring-1 ring-instagram-screen">
                <Avatar crestUrl={crestUrl} clubName={clubName} />
              </span>
              <span className="line-clamp-2 min-w-0 flex-1 whitespace-normal break-words font-semibold leading-5">{clubName}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full overflow-hidden rounded-xl border border-instagram-line bg-instagram-screen text-instagram-ink shadow-sm">
      <div className="flex h-[52px] items-center gap-3 px-3">
        <span className="w-10 shrink-0"><Avatar crestUrl={crestUrl} clubName={clubName} /></span>
        <span className="line-clamp-2 min-w-0 flex-1 whitespace-normal break-words text-[14px] font-semibold leading-5">{clubName}</span>
        <span className="text-sm font-bold">···</span>
      </div>
      <ScaledLivePreview key={previewKey} src={previewUrl} title={title} height={1350} />
      <div className="flex h-[52px] items-center justify-between px-3">
        <div className="flex items-center gap-3">
          <Heart className="h-6 w-6" />
          <MessageCircle className="h-6 w-6" />
          <Send className="h-6 w-6" />
        </div>
        <Bookmark className="h-6 w-6" />
      </div>
    </div>
  );
}